import React, { useEffect, useState, useContext } from 'react';

import { Loader } from '../components/crm/shared/CommonItems';
import { HandleAddActivity, HandleRemoveActivity } from '../components/crm/requests/ActivityRequests';
import { HandleAddNote, HandleRemoveNote, HandleUpdateNote } from '../components/crm/requests/NoteRequests';
import { HandleAddFile, HandleRemoveFile } from '../components/crm/requests/FileRequests';
import { GetOrganizationHistory, GetActiveOrganization, GetOrganizationPersons } from '../components/crm/requests/OrganizationRequests';
import { GetDealsFromOrganization } from '../components/crm/requests/DealRequests';
import { ActivityPanel } from '../components/crm/shared/DetailContent';
import { updateOrganizationProps } from '../components/crm/shared/Functions';
import { CompletedHistoryFetch } from '../components/crm/shared/Functions';
import { HistoryPanel } from '../components/crm/shared/HistoryItems';
import { ItemDetails, createOrganizationFieldComponents, createOrganizationRelationComponents } from '../components/crm/shared/ItemDetails';
import { DealsSummary } from '../components/crm/shared/DealsSummary';
import PersonsSummary from '../components/crm/shared/PersonsSummary';
import Toasts from '../components/crm/shared/Toasts';

import { DeepCopy } from '../components/tables/shared/TableEventHelpers';
import { scrollToHistoryAnchor } from '../components/crm/shared/field_inputs/shared/EventHelpers';

import { DataContext } from '../stores/crm-organization-store';
import { DataContext as SharedDataContext } from '../stores/crm-store';
import ErrorPage from './error';
import DetailHeader from '../components/crm/shared/DetailHeader';
import { ITEM_TITLE_DETAILS, ITEM_TITLE_ORGANIZATION, ITEM_TITLE_RELATIONS, REMOVE_ACTIVITY_WARNING, REMOVE_FILE_WARNING, REMOVE_NOTE_WARNING } from '../i18n/keyConstants';
import { useTranslation } from 'react-i18next';

const CrmOrganizationDetails = ({ id }) => {
    const {
        store: { activeOrganization, activityDealAlternatives, rows },
        dispatch,
    } = useContext(DataContext);
    const { store: sharedStore, dispatch: sharedDispatch } = useContext(SharedDataContext);

    const [error] = useState(null);
    const [, setIsSaving] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isLoadingHistory, setIsLoadingHistory] = useState(false);
    const [isLoadingDeals, setIsLoadingDeals] = useState(false);
    const [isLoadingPersons, setIsLoadingPersons] = useState(false);
    const [activeContent, setActiveContent] = useState(0);
    const [deals, setDeals] = useState([]);
    const [activeHistoryContent, setActiveHistoryContent] = useState(0);
    const [organizationPersons, setOrganizationPersons] = useState([]);
    const [historyCollection, setHistoryCollection] = useState({
        activities: [],
        notes: [],
        files: [],
        changelogs: [],
    });
    const { t } = useTranslation(['itemDetails', 'removeContent', 'organizationDetails']);
    const currentPath = window.location.pathname;

    /*
     * ===================================================================================================
     *                                       FUNCTIONS
     * ===================================================================================================
     */

    async function finishedOrganizationFetch(res) {
        if (res.success) {
            dispatch({
                type: 'UPDATE_ACTIVE_ORGANIZATION',
                payload: res.data,
            });
        }
        setIsLoading(false);
    }

    const finishedHistoryFetch = (res) => {
        if (res.success) {
            res = CompletedHistoryFetch(res, sharedStore);
            setHistoryCollection({
                activities: res.data.activities,
                notes: res.data.notes,
                files: res.data.files,
                changelogs: res.data.changelogs,
            });
        }

        setIsLoadingHistory(false);
    };

    const finishedPersonsFetch = (res) => {
        if (res.success) {
            setOrganizationPersons(res.data);
        }

        setIsLoadingPersons(false);
    };

    const finishedDealsFetch = (res) => {
        if (res.success) {
            setDeals(res.data);
            dispatch({
                type: 'UPDATE_ACTIVITY_DEAL_ALTERNATIVES',
                payload: res.data,
            });
        }

        setIsLoadingDeals(false);
    };

    async function PostActivity(activity) {
        const newOrganization = DeepCopy(activeOrganization);

        activity.organizationId = activeOrganization.id;
        activity.ownerId = activeOrganization.ownerId;
        activity.activityPersons = [];
        for (let i = 0; i < activity.persons.length; i++) {
            activity.activityPersons[i] = {
                type: 1,
                personId: activity.persons[i].id,
            };
        }

        setIsSaving(true);
        const response = await HandleAddActivity(activity, setIsSaving);

        if (response.success) {
            const innerActivity = response.data;
            innerActivity.organization = activeOrganization;

            newOrganization.activities.push(innerActivity);

            dispatch({
                type: 'UPDATE_ACTIVE_ORGANIZATION',
                payload: newOrganization,
            });
            sharedDispatch({ type: 'SHOW_SAVED_TOAST' });
        } else {
            sharedDispatch({ type: 'SHOW_ERROR_TOAST' });
        }
    }

    async function PostNote(note) {
        const newOrganization = DeepCopy(activeOrganization);

        note.organizationId = activeOrganization.id;

        setIsSaving(true);
        const response = await HandleAddNote(note);
        setIsSaving(false);

        if (response.success) {
            const innerNote = response.data;

            newOrganization.notes.push(innerNote);

            dispatch({ type: 'UPDATE_ACTIVE_ORGANIZATION', payload: newOrganization });
            sharedDispatch({ type: 'SHOW_SAVED_TOAST' });
        } else {
            sharedDispatch({ type: 'SHOW_ERROR_TOAST' });
        }
    }

    async function RemoveNote(note) {
        const confirm = window.confirm(t(REMOVE_NOTE_WARNING, { ns: 'removeContent' }));

        if (confirm) {
            const newOrganization = DeepCopy(activeOrganization);

            setIsSaving(true);
            const response = await HandleRemoveNote(note);
            setIsSaving(false);

            if (response.success) {
                const innerNote = response.data;

                newOrganization.notes.splice(newOrganization.notes.indexOf(innerNote), 1);

                dispatch({ type: 'UPDATE_ACTIVE_ORGANIZATION', payload: newOrganization });
                sharedDispatch({ type: 'SHOW_SAVED_TOAST' });
            } else {
                sharedDispatch({ type: 'SHOW_ERROR_TOAST' });
            }
        }
    }

    async function RemoveActivity(activity) {
        const confirm = window.confirm(t(REMOVE_ACTIVITY_WARNING, { ns: 'removeContent' }));

        if (confirm) {
            const newOrganization = DeepCopy(activeOrganization);

            setIsSaving(true);
            const response = await HandleRemoveActivity(activity, setIsSaving);

            if (response.success) {
                const innerActivity = response.data;

                newOrganization.activities.splice(newOrganization.activities.indexOf(innerActivity), 1);

                dispatch({ type: 'UPDATE_ACTIVE_ORGANIZATION', payload: newOrganization });
                sharedDispatch({ type: 'SHOW_SAVED_TOAST' });
            } else {
                sharedDispatch({ type: 'SHOW_ERROR_TOAST' });
            }
        }
    }

    async function UpdateNote(newText, note) {
        let newNote = DeepCopy(note);
        newNote.text = newText;

        setIsSaving(true);
        let response = await HandleUpdateNote(newNote);
        setIsSaving(false);
        if (response.success) {
            sharedDispatch({ type: 'SHOW_SAVED_TOAST' });
        } else {
            sharedDispatch({ type: 'SHOW_ERROR_TOAST' });
        }

        return response;
    }

    async function PostFile(file) {
        let newOrg = DeepCopy(activeOrganization);

        file['orgId'] = activeOrganization.id;
        file['ownerId'] = activeOrganization.ownerId;

        setIsSaving(true);
        let response = await HandleAddFile(file);
        setIsSaving(false);

        if (response.success) {
            let file = response.data;

            newOrg.files.push(file);

            dispatch({ type: 'UPDATE_ACTIVE_ORGANIZATION', payload: newOrg });
            sharedDispatch({ type: 'SHOW_SAVED_TOAST' });
        } else {
            sharedDispatch({ type: 'SHOW_ERROR_TOAST' });
        }
    }

    async function DownloadFile(file) {
        let url = `api/crmFileFront/${file.id}/download`;

        var link = document.createElement('a');
        link.setAttribute('target', '_blank');
        link.download = file.name;
        link.href = url;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    }

    async function RemoveFile(file) {
        var confirm = window.confirm(t(REMOVE_FILE_WARNING));

        if (confirm) {
            let newOrg = DeepCopy(activeOrganization);

            setIsSaving(true);
            let response = await HandleRemoveFile(file);
            setIsSaving(false);

            if (response.success) {
                let file = response.data;

                newOrg.files.splice(newOrg.files.indexOf(file), 1);

                dispatch({ type: 'UPDATE_ACTIVE_ORGANIZATION', payload: newOrg });
                sharedDispatch({ type: 'SHOW_DELETED_TOAST' });
            } else {
                sharedDispatch({ type: 'SHOW_ERROR_TOAST' });
            }
        }
    }

    const updateOrganization = async (newProps) => {
        const response = await updateOrganizationProps(activeOrganization, newProps);
        if (response.success) {
            updateStore(response.organization);
            dispatch({
                type: 'UPDATE_ACTIVE_ORGANIZATION',
                payload: response.organization,
            });
        }
        return response;
    };

    const updateStore = (newOrganization) => {
        //if (fetchSettings !== null) {     // todo: is the if-statement needed?
        const indexToUpdate = rows.findIndex((p) => p.id === newOrganization.id);
        rows[indexToUpdate] = newOrganization;

        if (indexToUpdate >= 0) {
            dispatch({ type: 'UPDATE_DATA', payload: rows });
        }
        //}
    };

    const scrollToHistory = () => {
        const anchor = sharedStore.anchorTag;
        if (anchor) {
            const anchorEl = document.getElementById(anchor);
            if (anchorEl) {
                anchorEl.scrollIntoView();
                sharedDispatch({
                    type: 'SET_ANCHOR_TAG',
                    payload: null,
                });
            }
        }
    };

    /*
     * ===================================================================================================
     *                                       EFFECTS
     * ===================================================================================================
     */

    useEffect(() => {
        if (activeOrganization === null || activeOrganization.id !== id) {
            setIsLoading(true);
            GetActiveOrganization(id, finishedOrganizationFetch);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        async function loadSections() {
            if (activeOrganization !== null && activeOrganization.id === id) {
                setIsLoadingDeals(true);
                await GetDealsFromOrganization(activeOrganization.id, finishedDealsFetch);

                setIsLoadingHistory(true);
                await GetOrganizationHistory(activeOrganization.id, finishedHistoryFetch);

                setIsLoadingPersons(true);
                await GetOrganizationPersons(activeOrganization['id'], finishedPersonsFetch);
            }
        }

        loadSections();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeOrganization]);

    useEffect(() => {
        if (activeOrganization === null || activeOrganization.id !== id) {
            setIsLoading(true);
            GetActiveOrganization(id, finishedOrganizationFetch);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPath]);

    useEffect(() => {
        const anchor = sharedStore.anchorTag;
        if (!isLoadingHistory && anchor) {
            scrollToHistoryAnchor(anchor, sharedDispatch);
        }
    }, [isLoadingHistory, sharedStore.anchorTag]);

    /*
     * ===================================================================================================
     *                                       MAIN COMPONENT
     * ===================================================================================================
     */

    if (error) {
        return <ErrorPage error={error} />;
    } else if (isLoading) {
        return (
            <section>
                <Loader />
            </section>
        );
    } else if (!activeOrganization) {
        return null;
    }

    return (
        <>
            <section>
                <div className='d-flex flex-column h-100 crm-container-details'>
                    <DetailHeader item={activeOrganization} updateItemProps={updateOrganization} itemType='organization' />
                    <div className='d-flex flex-row'>
                        <div className='d-flex flex-column detail-card-info'>
                            <ItemDetails
                                title={t(ITEM_TITLE_DETAILS, { ns: 'itemDetails' })}
                                components={createOrganizationFieldComponents(activeOrganization, sharedStore, updateOrganization, t)}
                                type={t(ITEM_TITLE_DETAILS, { ns: 'itemDetails' })}
                            />
                            {isLoadingDeals ? <Loader /> : <DealsSummary item={activeOrganization} deals={deals} />}
                            {isLoadingPersons ? (
                                <Loader />
                            ) : (
                                <PersonsSummary title='Personer' persons={organizationPersons} setPersons={setOrganizationPersons} />
                            )}
                            <ItemDetails
                                title={
                                    t(ITEM_TITLE_RELATIONS, { ns: 'organizationDetails' }) + ' (' +
                                    ((activeOrganization.parentOrganizationId != null ? 1 : 0) +
                                        activeOrganization.childOrganizations.length) +
                                    ')'
                                }
                                components={createOrganizationRelationComponents(activeOrganization, sharedStore, updateOrganization, t)}
                                type={t(ITEM_TITLE_ORGANIZATION)}
                            />
                        </div>
                        <div className='d-flex flex-column detail-card-content'>
                            <ActivityPanel
                                activeContent={activeContent}
                                activeItem={activeOrganization}
                                setActiveContent={setActiveContent}
                                dealAlternatives={activityDealAlternatives}
                                PostActivity={PostActivity}
                                PostNote={PostNote}
                                PostFile={PostFile}
                            />
                            {isLoadingHistory ? (
                                <Loader />
                            ) : (
                                <HistoryPanel
                                    historyCollection={historyCollection}
                                    activeItem={activeOrganization}
                                    activeHistoryContent={activeHistoryContent}
                                    setActiveHistoryContent={setActiveHistoryContent}
                                    removeNote={RemoveNote}
                                    updateNote={UpdateNote}
                                    removeActivity={RemoveActivity}
                                    removeFile={RemoveFile}
                                    downloadFile={DownloadFile}
                                />
                            )}
                        </div>
                    </div>
                </div>
            </section>
            <Toasts />
        </>
    );
};

export default CrmOrganizationDetails;
